import React, { useState, useCallback, useEffect, useRef, useMemo, useLayoutEffect } from "react";
import { DateTime } from "luxon"
import { Link } from "react-router-dom"

import ModalViewSignature from "./ModalViewSignature"
import { APIError, useAxios } from "../core/_axios";
import { Service, ServiceSignature } from "../core/_models";
import {
    getServiceSignaturesByUniqueIdentifier,
    retranscribeSignatureByUniqueIdentifier,
} from "../core/_requests";
import LoadingSpinner from "./LoadingSpinner";
import { useAuth } from "../AuthContext";
import useAccountChannel from "../hooks/useAccountChannel";
import { getSignatures } from "../core/_requests_v2";
import useLocalizedCopy from "../hooks/useLocalizedCopy";

const localStorageKeyToOnlyShowPreneedItems = 'onlyShowPreNeedItems';

export function SignaturesList({
    service
}: {
    service?: Service
}) {

    const {
        currentUser,
        viewingAllAccounts
    } = useAuth();
    
    const { copy } = useLocalizedCopy();

    const axios = useAxios();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState(false);

    const [signatures, setSignatures] = useState<ServiceSignature[]>([]);

    const [viewingSignature, setViewingSignature] = useState<ServiceSignature | null>(null);

    const [totalSignatures, setTotalSignatures] = useState<null | number>(null);

    const [retranscribingSignature, setRetranscribingSignature] = useState<ServiceSignature | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [errorWithTranscriptionItem, setErrorWithTranscriptionItem] = useState<{
        error: string,
        transcriptionItem: ServiceSignature
    } | null>(null);

    const itemsPerPage = 50; // set in the backend right now

    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);

    const [onlyShowPreNeedItems, setOnlyShowPreNeedItems] = useState(localStorage.getItem(localStorageKeyToOnlyShowPreneedItems) === 'true');
    
    // Search functionality - re-enabled with proper focus handling
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const shouldMaintainFocus = useRef(false);

    // Memoized placeholder to prevent unnecessary re-renders
    const searchPlaceholder = useMemo(() => {
        return totalSignatures ? `Search ${totalSignatures.toLocaleString()} signatures...` : 'Search signatures...';
    }, [totalSignatures]);

    // Restore focus after DOM updates
    useLayoutEffect(() => {
        if (shouldMaintainFocus.current && searchInputRef.current && !loading) {
            searchInputRef.current.focus();
            shouldMaintainFocus.current = false;
        }
    }, [loading, signatures]);

    // Reset to first page when search query changes
    useEffect(() => {
        if (searchQuery !== '') {
            setCurrentPage(1);
        }
    }, [searchQuery]);

    // Cleanup debounce timer
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    useAccountChannel({
        onSignatureUpdated: (reasonForUpdate, signature) => {
            if (service && (signature.service.unique_identifier !== service.unique_identifier)) {
                console.warn('Signature update received for a different service than the one being viewed:', signature.service.unique_identifier, service.unique_identifier);
                // If the signature is not for the current service, ignore it
                // TODO: Fix this in the backend, this is a temporary hack
                return;
            }
            if (reasonForUpdate === 'created_by_ipad' || reasonForUpdate === 'created_by_qr_code') {
                // Add the signature to the list
                if (reasonForUpdate === 'created_by_ipad') {
                    if (onlyShowPreNeedItems && !signature.public_data.receive_information_on_advance_planning) {
                        return;
                    }
                } else if (reasonForUpdate === 'created_by_qr_code') {
                    // The QR code stores the signature differently than the iPad
                    if (onlyShowPreNeedItems && !signature.public_data.based_on_simple_signature?.interested_in_planning_ahead) {
                        return;
                    }
                }
                setSignatures((signatures) => {
                    return [signature, ...signatures];
                });

            } else {
                setSignatures((signatures) => {
                    return signatures.map((existingSignature) => {
                        if (existingSignature.unique_identifier === signature.unique_identifier) {
                            return signature;
                        }
                        return existingSignature;
                    });
                });
            }
        },
        onSignatureRemoved: (reasonForRemoval, signature) => {
            setSignatures((signatures) => {
                return signatures.filter((existingSignature) => {
                    return existingSignature.unique_identifier !== signature.unique_identifier;
                });
            });
        }
    });

    const loadSignatures = useCallback(
        async (loadPage: number = 1) => {
            const isLoadingMore = loadPage > 1;
            if (isLoadingMore) {
                setLoadingMore(true);
                setCurrentPage(loadPage);
            } else {
                setLoading(true);
                setSignatures([]); // Reset signatures when loading the first page
            }
            setLoadingError(null);
            try {
                if (service) {
                    const {
                        signatures: updatedServiceSignatures,
                        signatures_count: updatedServiceSignaturesCount,
                    } = await getServiceSignaturesByUniqueIdentifier(
                        axios,
                        service.unique_identifier,
                        loadPage,
                        onlyShowPreNeedItems,
                        searchQuery
                    );
                    // If loadPage > 1, append signatures, otherwise set them directly
                    setSignatures((prevSignatures) =>
                        isLoadingMore
                            ? [...prevSignatures, ...updatedServiceSignatures]
                            : updatedServiceSignatures
                    );
                    setTotalSignatures(updatedServiceSignaturesCount);
                    setHasMore(updatedServiceSignatures.length === itemsPerPage);
                } else {
                    // Use the V2 API for both search and non-search
                    const {
                        signatures: updatedSignatures,
                        total_signatures: updatedTotalSignatures,
                    } = await getSignatures(axios, loadPage, onlyShowPreNeedItems, ''); // Always pass empty string for search since V2 search is broken
                    
                    let filteredSignatures = updatedSignatures;
                    let filteredTotalCount = updatedTotalSignatures;
                    
                    // If there's a search query, filter the results client-side
                    if (searchQuery && searchQuery.trim() !== '') {
                        const searchTerm = searchQuery.toLowerCase();
                        filteredSignatures = updatedSignatures.filter(signature => {
                            // Search in signer name
                            const signerName = signature.public_data.signer_info?.name || 
                                             signature.public_data.latest_signature_transcription?.generated_json?.name || 
                                             signature.public_data.based_on_simple_signature?.full_name || '';
                            
                            // Search in signer email
                            const signerEmail = signature.public_data.signer_info?.email || 
                                              signature.public_data.latest_signature_transcription?.generated_json?.email || 
                                              signature.public_data.based_on_simple_signature?.email_address || '';
                            
                            // Search in service name (deceased person)
                            const serviceName = signature.service?.public_data?.deceased_person?.full_name || '';
                            
                            // Search in account name
                            const accountName = signature.service?.account?.name || '';
                            
                            return signerName.toLowerCase().includes(searchTerm) ||
                                   signerEmail.toLowerCase().includes(searchTerm) ||
                                   serviceName.toLowerCase().includes(searchTerm) ||
                                   accountName.toLowerCase().includes(searchTerm);
                        });
                        
                        filteredTotalCount = filteredSignatures.length;
                        // For search, don't enable pagination since we're filtering client-side
                        setHasMore(false);
                    } else {
                        // For non-search, use normal pagination
                        setHasMore(updatedSignatures.length === itemsPerPage);
                    }
                    
                    // Same logic: append or reset based on loadPage
                    setSignatures((prevSignatures) =>
                        isLoadingMore
                            ? [...prevSignatures, ...filteredSignatures]
                            : filteredSignatures
                    );
                    setTotalSignatures(filteredTotalCount);
                }
            } catch (exception) {
                console.error(exception);
                const errorMessage =
                    exception instanceof APIError
                        ? exception.message
                        : 'An unknown error has occurred. Please try again.';
                setLoadingError(errorMessage);
            } finally {
                if (isLoadingMore) {
                    setLoadingMore(false);
                } else {
                    setLoading(false);
                }
            }
        },
        [axios, onlyShowPreNeedItems, service, searchQuery]
    );

    // Load signatures when component mounts or dependencies change
    useEffect(() => {
        loadSignatures();
    }, [loadSignatures]);

    const onRetranscribeSignature = useCallback(async (signature: ServiceSignature) => {
        setRetranscribingSignature(signature);
        setErrorWithTranscriptionItem(null);
        try {
            const updatedSignature = await retranscribeSignatureByUniqueIdentifier(axios, signature.unique_identifier)
            setSignatures((signatures) => {
                return signatures.map((existingSignature) => {
                    if (existingSignature.unique_identifier === updatedSignature.unique_identifier) {
                        return updatedSignature;
                    }
                    return existingSignature;
                });
            });
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setErrorWithTranscriptionItem({
                error: errorMessage,
                transcriptionItem: signature
            })
        } finally {
            setRetranscribingSignature(null);
        }
    }, [axios]);

    const onDownloadCurrentListToCSV = useCallback(() => {
        const currentItems = signatures.map((signature) => {
            // address is either const [manually_inputted_addressLine1, setManuallyInputtedAddressLine1] = useState<string>(signature.public_data.latest_signature_transcription?.generated_json?.mailing_address_line_1 || '');
            // or: {signature.public_data.based_on_simple_signature.mailing_address_line1 || 'N/A'}
            const addressLine1 = signature.public_data.latest_signature_transcription?.generated_json?.mailing_address_line_1 || signature.public_data.based_on_simple_signature?.mailing_address_line1 || '';
            const addressLine2 = signature.public_data.latest_signature_transcription?.generated_json?.mailing_address_line_2 || signature.public_data.based_on_simple_signature?.mailing_address_line2 || '';
            return {
                [copy.Common.labels.name]: signature.public_data.signer_info?.name || signature.public_data.latest_signature_transcription?.generated_json?.name || signature.public_data.based_on_simple_signature?.full_name || '',
                [copy.Common.labels.phone]: signature.public_data.signer_info?.phone || signature.public_data.latest_signature_transcription?.generated_json?.phone_number || signature.public_data.based_on_simple_signature?.phone_number || '',
                [copy.Common.labels.email]: signature.public_data.signer_info?.email || signature.public_data.latest_signature_transcription?.generated_json?.email || signature.public_data.based_on_simple_signature?.email_address || '',
                [copy.Common.labels.address]: `"${addressLine1} ${addressLine2}"`.trim(),
                [copy.Services.signatures.type]: signature.signature_type ? 
                    (signature.signature_type === 'ipad' ? 'iPad' : 
                     signature.signature_type === 'qr_code' ? 'QR Code' : 'Email') :
                    // Fallback for signatures without explicit type
                    (signature.public_data.created_from_email_link ? 'Email' : 
                     signature.public_data.based_on_simple_signature ? 'QR Code' : 'iPad'),
                [copy.Public.sign.preplanningOption]: signature.public_data.receive_information_on_advance_planning || signature.public_data.latest_signature_transcription?.generated_json.receive_information_on_advance_planning || signature.public_data.based_on_simple_signature?.interested_in_planning_ahead ? copy.Common.buttons.yes : copy.Common.buttons.no,
                [copy.Common.labels.status]: signature.public_data.mogul_bridge_sync ? copy.Common.buttons.yes : copy.Common.buttons.no,
                [copy.Services.view.deceasedName]: signature.service.public_data.deceased_person.full_name,
                'iPad': signature.created_by_ipad ? signature.created_by_ipad.public_data.ipad_name : '',
                [copy.Common.navigation.account]: signature.service.account.name,
                [copy.Services.list.created]: DateTime.fromISO(signature.inserted_at).toFormat('yyyy-MM-dd HH:mm:ss'),
                [copy.Services.signatures.viewSignature]: signature.public_data.screen_capture_url || '',
            }
        });
        const csv = [
            Object.keys(currentItems[0]).join(','),
            ...currentItems.map((item) => Object.values(item).join(','))
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'signatures.csv';
        link.click();
        URL.revokeObjectURL(url);
    }, [service, signatures, viewingAllAccounts, copy.Common.labels.address, copy.Common.labels.email, copy.Common.labels.name, copy.Common.labels.phone, copy.Common.navigation.account, copy.Services.list.created, copy.Services.signatures.type, copy.Services.view.deceasedName]);

    if (loading) {
        return (
            <LoadingSpinner />
        );
    }

    if (loadingError) {
        return (
            <div>
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={() => {
                        setLoadingError(null);
                        loadSignatures();
                    }}>
                        {copy.Common.messages.tryAgain}
                    </button>
                </div>
            </div>
        );
    }

    return (<div className="grid gap-5 lg:gap-7.5">
        <div className="card card-grid min-w-full">
            <div className="card-header flex-wrap py-3">
                {/* Search input */}
                <div className="relative flex-1 max-w-md">
                    <i className="ki-filled ki-magnifier leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3"></i>
                    <input
                        ref={searchInputRef}
                        id="signatures-search-input"
                        key="signatures-search-input"
                        type="text"
                        className="input input-sm pl-8 w-full"
                        placeholder={searchPlaceholder}
                        value={searchInput}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        onChange={(e) => {
                            const value = e.target.value;
                            setSearchInput(value);
                            
                            // Only track focus if input is currently focused (user is actively typing)
                            if (document.activeElement === searchInputRef.current) {
                                shouldMaintainFocus.current = true;
                            }
                            
                            // Debounce search
                            if (debounceTimer.current) {
                                clearTimeout(debounceTimer.current);
                            }
                            
                            debounceTimer.current = setTimeout(() => {
                                setSearchQuery(value);
                            }, 300);
                        }}
                    />
                    {searchInput && (
                        <button
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            onClick={() => {
                                // Clear the debounced search timer first
                                if (debounceTimer.current) {
                                    clearTimeout(debounceTimer.current);
                                }
                                
                                setSearchQuery('');
                                setSearchInput('');
                                
                                // Set flag to restore focus after the API call and re-render
                                shouldMaintainFocus.current = true;
                            }}
                        >
                            <i className="ki-filled ki-cross-circle"></i>
                        </button>
                    )}
                </div>

                
                <div className="flex gap-6">
                    <label className="switch switch-sm">
                        <input
                            className="order-2"
                            name="check"
                            type="checkbox"
                            checked={onlyShowPreNeedItems}
                            onChange={() => {
                                const updatedValue = !onlyShowPreNeedItems;
                                setOnlyShowPreNeedItems(updatedValue);
                                // This sets `localStorage` to remember the state of the checkbox
                                if (updatedValue) {
                                    localStorage.setItem(localStorageKeyToOnlyShowPreneedItems, 'true');
                                } else {
                                    localStorage.removeItem(localStorageKeyToOnlyShowPreneedItems);
                                }
                            }}
                        />
                        <span className="switch-label order-1">
                            {copy.Public.sign.interestedInPlanningLabel}
                        </span>
                    </label>
                    {true && 'downloadToCSVButton' && <div>
                        <button className="btn btn-sm btn-light" onClick={onDownloadCurrentListToCSV}>
                            {copy.Services.signatures.exportCSV}
                        </button>
                    </div>}
                </div>
            </div>
            <div className="card-body">
                <div data-datatable="true" data-datatable-page-size="10" id="teams_table">
                    <div className="scrollable-x-auto">
                        <table className="table table-fixed table-border" data-datatable-table="true">
                            {signatures.length > 0 && <thead>
                                <tr>
                                    <th className="w-[360px]">
                                        <span className="sort asc">
                                            <span className="sort-label">
                                                {copy.Common.navigation.signatures}
                                            </span>
                                            {false && <span className="sort-icon">
                                            </span>}
                                        </span>
                                    </th>
                                    <th className="w-[60px]">
                                        <span className="sort">
                                            <span className="sort-label">
                                                {copy.Services.signatures.type}
                                            </span>
                                            {false && <span className="sort-icon">
                                            </span>}
                                        </span>
                                    </th>
                                    <th className="w-[100px] text-center">
                                        <span className="sort">
                                            <span className="sort-label">
                                                {copy.Public.sign.preplanningOption}
                                            </span>
                                            {false && <span className="sort-icon">
                                            </span>}
                                        </span>
                                    </th>
                                    <th className="w-[100px] text-center">
                                        <span className="sort">
                                            <span className="sort-label">
                                                {copy.Common.labels.status}
                                            </span>
                                            {false && <span className="sort-icon">
                                            </span>}
                                        </span>
                                    </th>
                                    <th className="w-[350px]">
                                        <span className="sort asc">
                                            <span className="sort-label">
                                                {copy.Services.view.deceasedName}
                                            </span>
                                            {false && <span className="sort-icon">
                                            </span>}
                                        </span>
                                    </th>
                                    <th className="w-[200px]">
                                        <span className="sort asc">
                                            <span className="sort-label">
                                                iPad
                                            </span>
                                            {false && <span className="sort-icon">
                                            </span>}
                                        </span>
                                    </th>
                                    {viewingAllAccounts && !service && <th className="w-[200px]">
                                        {copy.Common.navigation.account}
                                    </th>}
                                    <th className="w-[200px]">
                                        <span className="sort">
                                            <span className="sort-label">
                                                {copy.Services.list.created}
                                            </span>
                                            {false && <span className="sort-icon">
                                            </span>}
                                        </span>
                                    </th>
                                    {(currentUser.is_agency_admin || currentUser.is_super_super_user) && <th className="w-[130px]">
                                        {copy.Services.signatures.actions}
                                    </th>}
                                </tr>
                            </thead>}
                            {signatures.length > 0 ? <tbody>
                                {signatures.map((signature) => {
                                    return <tr key={signature.unique_identifier} data-unqiue-identifier={signature.unique_identifier}>
                                        {/*errorWithTranscriptionItem?.transcriptionItem.unique_identifier === signature.unique_identifier && <tr>
                                            <td colSpan={6} className="text-center">
                                                <div>
                                                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                                                        <span className="block sm:inline">
                                                            {errorWithTranscriptionItem.error}
                                                        </span>
                                                    </div>
                                                    <div className="mt-5">
                                                        <button
                                                            className="btn btn-sm btn-light"
                                                            onClick={() => setErrorWithTranscriptionItem(null)}
                                                        >
                                                            Close
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>*/}
                                        <td>
                                            <div className="flex flex-col gap-1.5">
                                                <div className="flex flex-col justify-center gap-2" onClick={() => setViewingSignature(signature)}>
                                                    {signature.public_data.latest_signature_transcription?.generated_json ? <>
                                                        <div className="leading-none font-semibold text-sm text-gray-900 hover:text-primary cursor-pointer">
                                                            {signature.public_data.latest_signature_transcription.generated_json.name || `(${copy.Common.labels.name} ${copy.Common.messages.noData.toLowerCase()})`}
                                                        </div>
                                                        <div className="text-2sm text-gray-600">
                                                            {`${signature.public_data.latest_signature_transcription.generated_json.phone_number || `(${copy.Common.labels.none})`} | ${signature.public_data.latest_signature_transcription.generated_json.email || `(${copy.Common.labels.none})`}`}
                                                        </div>
                                                    </> : <>
                                                        {signature.public_data.created_from_email_link && signature.public_data.signer_info ? <>
                                                            <div className="leading-none font-semibold text-sm text-gray-900 hover:text-primary cursor-pointer">
                                                                {signature.public_data.signer_info.name || `(${copy.Common.labels.name} ${copy.Common.messages.noData.toLowerCase()})`}
                                                            </div>
                                                            <div className="text-2sm text-gray-600">
                                                                {`${signature.public_data.signer_info.phone || `(${copy.Common.labels.none})`} | ${signature.public_data.signer_info.email || `(${copy.Common.labels.none})`}`}
                                                            </div>
                                                        </> : signature.public_data.based_on_simple_signature ? <>
                                                            <div className="leading-none font-semibold text-sm text-gray-900 hover:text-primary cursor-pointer">
                                                                {signature.public_data.based_on_simple_signature.full_name || `(${copy.Common.labels.name} ${copy.Common.messages.noData.toLowerCase()})`}
                                                            </div>
                                                            <div className="text-2sm text-gray-600">
                                                                {`${signature.public_data.based_on_simple_signature.phone_number || `(${copy.Common.labels.none})`} | ${signature.public_data.based_on_simple_signature.email_address || `(${copy.Common.labels.none})`}`}
                                                            </div>
                                                        </> : <>
                                                            {/* This happens before AI transcribes the signature */}
                                                            {copy.Public.family.waitingForTranscription}
                                                        </>}
                                                    </>}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex flex-col items-center justify-center gap-1.5 w-full">
                                                {signature.signature_type ? (
                                                    // Use explicit signature type
                                                    signature.signature_type === 'email' ?
                                                        <span className="menu-icon items-start text-gray-500 dark:text-gray-400 w-[20px]">
                                                            <i className={`ki-filled ki-sms text-lg`}></i>
                                                        </span> :
                                                    signature.signature_type === 'qr_code' ?
                                                        <span className="menu-icon items-start text-gray-500 dark:text-gray-400 w-[20px]">
                                                            <i className={`ki-filled ki-phone text-lg`}></i>
                                                        </span> :
                                                        <span className="menu-icon items-start text-gray-500 dark:text-gray-400 w-[20px]">
                                                            <i className={`ki-filled ki-brush text-lg`}></i>
                                                        </span>
                                                ) : (
                                                    // Fallback for signatures without explicit type
                                                    signature.public_data.created_from_email_link ?
                                                        <span className="menu-icon items-start text-gray-500 dark:text-gray-400 w-[20px]">
                                                            <i className={`ki-filled ki-sms text-lg`}></i>
                                                        </span> :
                                                    signature.public_data.based_on_simple_signature ?
                                                        <span className="menu-icon items-start text-gray-500 dark:text-gray-400 w-[20px]">
                                                            <i className={`ki-filled ki-phone text-lg`}></i>
                                                        </span> :
                                                        <span className="menu-icon items-start text-gray-500 dark:text-gray-400 w-[20px]">
                                                            <i className={`ki-filled ki-brush text-lg`}></i>
                                                        </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {/* Pre-Need? */}
                                            <div className="flex flex-col items-center justify-center gap-1.5 w-full">
                                                {signature.public_data.receive_information_on_advance_planning || signature.public_data.latest_signature_transcription?.generated_json.receive_information_on_advance_planning || signature.public_data.based_on_simple_signature?.interested_in_planning_ahead ?
                                                    <span className="flex w-3 h-3 bg-green-500 rounded-full"></span> :
                                                    <span className="flex w-3 h-3 bg-gray-300 rounded-full"></span>
                                                }
                                            </div>
                                        </td>
                                        <td>
                                            {/* Synced? */}
                                            <div className="flex flex-col items-center justify-center gap-1.5 w-full">
                                                {signature.public_data.mogul_bridge_sync ?
                                                    <span className="flex w-3 h-3 bg-green-500 rounded-full animate-pulse"></span> :
                                                    <span className="flex w-3 h-3 bg-gray-300 rounded-full"></span>
                                                }
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex flex-col gap-1.5">
                                                <div className="flex flex-row items-center gap-2">
                                                    <Link
                                                        to={`/services/${signature.service.unique_identifier}/signatures`}
                                                        className="leading-none font-semibold text-sm text-gray-900 hover:text-primary"
                                                    >
                                                        {signature.service.public_data.deceased_person.full_name}
                                                    </Link>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="flex flex-col gap-1.5">
                                                <div className="flex flex-row items-center gap-2">
                                                    {signature.created_by_ipad ? <>
                                                        <div data-ipad-unqiue-identifier={signature.created_by_ipad.unique_identifier}>
                                                            {signature.created_by_ipad.public_data.ipad_name || `(${copy.Common.labels.name} ${copy.Common.messages.noData.toLowerCase()})`}
                                                        </div>
                                                    </> : <>
                                                        -
                                                    </>}
                                                </div>
                                            </div>
                                        </td>
                                        {
                                            viewingAllAccounts && !service && <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <div className="flex flex-row items-center gap-2">
                                                        {signature.service.account.name}
                                                    </div>
                                                </div>
                                            </td>
                                        }
                                        <td>
                                            {DateTime.fromISO(signature.inserted_at).toRelative()}
                                        </td>
                                        {
                                            (currentUser.is_agency_admin || currentUser.is_super_super_user) && <td>
                                                {/* Only allow retranscribe for iPad signatures */}
                                                {(signature.signature_type === 'ipad' || 
                                                  (!signature.signature_type && !signature.public_data.based_on_simple_signature && !signature.public_data.created_from_email_link)) && <>
                                                    {!retranscribingSignature && <>
                                                        <button
                                                            className="btn btn-sm btn-light"
                                                            onClick={() => onRetranscribeSignature(signature)}
                                                        >
                                                            {copy.Services.signatures.retranscribe}
                                                        </button>
                                                    </>}
                                                </>}
                                            </td>
                                        }
                                    </tr>
                                })}
                            </tbody> : <tbody>
                                <tr>
                                    <td colSpan={1} className="text-center">
                                        <div>
                                            {copy.Services.signatures.noSignatures}
                                        </div>
                                    </td>
                                </tr>
                            </tbody>}
                        </table>
                    </div>
                </div>
            </div>
            <ModalViewSignature
                signature={viewingSignature}
                isOpen={!!viewingSignature}
                onCloseModal={() => {
                    setViewingSignature(null)
                }}
            />
            {hasMore && <div className="card-footer">
                <div className="flex justify-center">
                    <button
                        disabled={loadingMore}
                        className="btn btn-lg btn-light"
                        onClick={() => {
                            loadSignatures(currentPage + 1);
                        }}
                    >
                        {copy.Services.signatures.loadMore}
                    </button>
                </div>
            </div>}
        </div>
    </div>);
}